/**
 * Coin Fund Reserve — Phase A3 Admin Safety Dashboard
 * ====================================================
 *
 * READ-ONLY monitoring surface for the Coin Fund Reserve.
 *
 * Consumes: GET /api/admin/coin-reserve/dashboard
 *
 * Sections (Rev-2 §9 + A3 brief):
 *   1. Funding Safety     — YES/NO pill · S2 advisory mode · totals
 *   2. Coin Economy       — collected / unspent / consumed / paid /
 *                            reward + test (labeled NON-CASHABLE)
 *   3. RGM Retained       — gross / fees / losses / net (NOT profit)
 *   4. Historical Reconciliation — A2 counts + preview-key disclosure
 *   5. Ledger Audit       — recent events + snapshot / hash status
 *   + Warnings banner     — coverage < 1.0, negative surplus,
 *                            replay mismatch, stale snapshot,
 *                            dual-write / dead-letter, disputes
 *
 * Zero write controls. Every element is a passive read. Auth-gated
 * by the backend (admin_only) — the FE only renders what the API
 * returns.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";

// ── Small format helpers ────────────────────────────────────
const fmtUsdCents = (cents) => {
  if (cents === null || cents === undefined) return "—";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(Number(cents) || 0);
  return `${sign}$${(abs / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
const fmtNum = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtCoverage = (c) => {
  if (c === null || c === undefined) return "∞";
  return `${(Number(c) * 100).toFixed(1)}%`;
};

// ── Reusable primitives ─────────────────────────────────────
const Tile = ({ label, value, sub, tone = "default", testId }) => {
  const border = tone === "danger" ? "border-rose-500/40"
              : tone === "warn"    ? "border-amber-500/40"
              : "border-white/10";
  const valueColor = tone === "danger" ? "text-rose-200"
                   : tone === "warn"   ? "text-amber-200"
                   : "text-white";
  return (
    <div
      className={`rounded-lg bg-white/[0.03] border ${border} p-4`}
      data-testid={testId}
    >
      <p className="text-[11px] uppercase tracking-widest text-gray-400">{label}</p>
      <p className={`text-2xl font-black mt-1 ${valueColor}`}>{value ?? "—"}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
    </div>
  );
};

const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="mb-10" data-testid={`cr-section-${id}`}>
    <div className="mb-3">
      <h2 className="text-xl sm:text-2xl font-black text-white">{title}</h2>
      {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

// ── Page ────────────────────────────────────────────────────
export default function AdminCoinReservePage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.get("/admin/coin-reserve/dashboard");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load dashboard");
      if (e?.response?.status === 401) nav("/login");
    } finally {
      setBusy(false);
    }
  }, [nav]);

  useEffect(() => {
    (async () => {
      try { setMe((await api.get("/auth/me")).data); } catch (_e) { /* soft */ }
    })();
    load();
  }, [load]);

  const fs = data?.funding_safety;
  const ce = data?.coin_economy;
  const rgm = data?.rgm_retained;
  const rec = data?.reconciliation;
  const la  = data?.ledger_audit;
  const warnings = data?.warnings || [];
  const fullyFunded = !!fs?.artist_gift_liabilities_fully_funded;

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="admin-coin-reserve-page">
      <SharedNavigation currentUser={me} activePage="admin" />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3">
              🧾 <span>Coin Fund Reserve</span>
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Admin monitoring surface — read-only. S2 remains ADVISORY.
              S1 (verified paid-backed earnings) remains the sole enforced
              payout gate.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/control-center">
              <Button variant="outline" className="text-white border-gray-700"
                      data-testid="cr-back-to-cc">
                ← Control Center
              </Button>
            </Link>
            <Button onClick={load} disabled={busy}
                    className="bg-purple-600 hover:bg-purple-500 text-white"
                    data-testid="cr-refresh">
              {busy ? "Refreshing…" : "🔄 Refresh"}
            </Button>
          </div>
        </div>

        {err && (
          <div
            className="mb-6 p-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-100 text-sm"
            data-testid="cr-error"
          >{err}</div>
        )}

        {/* Warnings banner */}
        {warnings.length > 0 && (
          <div className="mb-8" data-testid="cr-warnings">
            {warnings.map((w) => {
              const tone = w.severity === "high"
                ? "border-rose-500/50 bg-rose-500/10 text-rose-100"
                : "border-amber-500/40 bg-amber-500/10 text-amber-100";
              return (
                <div key={w.code}
                     className={`mb-2 rounded-lg border p-3 text-sm ${tone}`}
                     data-testid={`cr-warning-${w.code}`}>
                  <span className="font-black mr-2">{w.severity === "high" ? "⛔" : "⚠️"}</span>
                  <span className="font-bold">{w.code}</span>: {w.message}
                </div>
              );
            })}
          </div>
        )}

        {/* 1. Funding Safety */}
        <Section id="funding-safety" title="1. Funding Safety"
                 subtitle="S2 advisory only — this pill never gates payouts in Phase A1/A3.">
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <span
              className={`text-sm font-black px-4 py-2 rounded-full ${
                fullyFunded
                  ? "bg-emerald-500/20 border border-emerald-400 text-emerald-100"
                  : "bg-rose-500/20 border border-rose-400 text-rose-100"
              }`}
              data-testid="cr-fully-funded-pill"
            >
              ARTIST GIFT LIABILITIES FULLY FUNDED: {fullyFunded ? "YES" : "NO"}
            </span>
            <span className="text-[11px] uppercase tracking-widest text-amber-200"
                  data-testid="cr-s2-mode">
              S2 mode: {fs?.s2_mode || "ADVISORY"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Tile label="Reconciled Reserve Assets"
                  value={fmtUsdCents(fs?.reserve_assets_cents)}
                  testId="cr-tile-reserve-assets" />
            <Tile label="Pending Artist Liability"
                  value={fmtUsdCents(fs?.liability_pending_cents)}
                  sub="7-day hold before eligible"
                  testId="cr-tile-liability-pending" />
            <Tile label="Available Artist Liability"
                  value={fmtUsdCents(fs?.liability_available_cents)}
                  sub="Cleared, awaiting payout"
                  testId="cr-tile-liability-available" />
            <Tile label="Total Artist Liability"
                  value={fmtUsdCents(fs?.liability_total_cents)}
                  testId="cr-tile-liability-total" />
            <Tile label="Funding Coverage"
                  value={fmtCoverage(fs?.funding_coverage)}
                  tone={(fs?.funding_coverage !== null && fs?.funding_coverage < 1) ? "danger" : "default"}
                  testId="cr-tile-coverage" />
            <Tile label={fs?.surplus_cents < 0 ? "Reserve Deficit" : "Reserve Surplus"}
                  value={fmtUsdCents(fs?.surplus_cents)}
                  tone={fs?.surplus_cents < 0 ? "danger" : "default"}
                  testId="cr-tile-surplus" />
          </div>
          <p className="mt-3 text-[11px] text-gray-500" data-testid="cr-s2-note">
            {fs?.s2_advisory_note}
          </p>
        </Section>

        {/* 2. Coin Economy */}
        <Section id="coin-economy" title="2. Coin Economy"
                 subtitle="Bookkeeping totals derived from the A1 ledger fold.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Tile label="Coin Purchase Funds Recorded"
                  value={fmtUsdCents(ce?.coin_purchase_funds_recorded_cents)}
                  sub="A1 ledger — coin_purchase_completed events"
                  testId="cr-tile-funds-recorded" />
            <Tile label="Unspent Paid-Coin Obligation"
                  value={`${fmtNum(ce?.unspent_paid_coin_obligation_coins)} coins`}
                  sub="Fans still hold these; RGM has already received the cash"
                  testId="cr-tile-unspent-obligation" />
            <Tile label="Paid Coins Consumed Through Gifts"
                  value={`${fmtNum(ce?.paid_coins_consumed_via_gifts)} coins`}
                  testId="cr-tile-consumed-via-gifts" />
            <Tile label="Paid to Artists"
                  value={fmtUsdCents(ce?.paid_to_artists_cents)}
                  testId="cr-tile-paid-to-artists" />
            <Tile
              label="Reward Coins (platform-wide)"
              value={`${fmtNum(ce?.reward_coins_platform_wide?.value_coins)} coins`}
              sub="NON-CASHABLE — never enters payout reserve"
              tone="warn"
              testId="cr-tile-reward-coins" />
            <Tile
              label="Test Coins (platform-wide)"
              value={`${fmtNum(ce?.test_coins_platform_wide?.value_coins)} coins`}
              sub="NON-CASHABLE — never enters payout reserve"
              tone="warn"
              testId="cr-tile-test-coins" />
          </div>
          {ce?.notes && (
            <ul className="mt-3 text-[11px] text-gray-500 list-disc pl-5 space-y-1"
                data-testid="cr-coin-economy-notes">
              {ce.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </Section>

        {/* 3. RGM Retained */}
        <Section id="rgm-retained" title="3. RGM Retained"
                 subtitle="Bookkeeping only — NOT profit.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Platform Cut GROSS"
                  value={fmtUsdCents(rgm?.rgm_retained_gross_cents)}
                  sub="25% of paid-coin gifts (BEFORE fees / losses)"
                  testId="cr-tile-rgm-gross" />
            <Tile label="Known Stripe Fees"
                  value={fmtUsdCents(rgm?.stripe_fees_known_cents)}
                  sub="Recognised via balance_transaction lookups"
                  testId="cr-tile-stripe-fees" />
            <Tile label="Refund / Dispute Losses"
                  value={fmtUsdCents(rgm?.refund_dispute_losses_cents)}
                  tone={rgm?.refund_dispute_losses_cents > 0 ? "warn" : "default"}
                  testId="cr-tile-refund-losses" />
            <Tile label="Platform NET Retained"
                  value={fmtUsdCents(rgm?.rgm_retained_net_cents)}
                  testId="cr-tile-rgm-net" />
          </div>
          <p className="mt-3 text-[11px] text-gray-500 italic"
             data-testid="cr-rgm-disclosure">
            {rgm?.disclosure}
          </p>
        </Section>

        {/* 4. Historical Reconciliation */}
        <Section id="reconciliation" title="4. Historical Reconciliation"
                 subtitle="A2 classifier totals. AMBIGUOUS/INVALID rows are NEVER counted as reserve assets.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Tile label={`VERIFIED (${fmtNum(rec?.verified)})`}
                  value={fmtUsdCents(rec?.verified_gross_cents)}
                  sub={`${fmtNum(rec?.verified_paid_coins)} paid + ${fmtNum(rec?.verified_bonus_coins)} bonus`}
                  testId="cr-tile-recon-verified" />
            <Tile label={`AMBIGUOUS (${fmtNum(rec?.ambiguous)})`}
                  value={fmtUsdCents(rec?.ambiguous_gross_cents)}
                  tone="warn"
                  testId="cr-tile-recon-ambiguous" />
            <Tile label={`INVALID (${fmtNum(rec?.invalid)})`}
                  value={fmtUsdCents(rec?.invalid_gross_cents)}
                  tone={rec?.invalid > 0 ? "danger" : "default"}
                  testId="cr-tile-recon-invalid" />
          </div>
          <div className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5"
               data-testid="cr-recon-disclosure">
            <p className="text-xs text-amber-100 leading-relaxed">
              <span className="font-black">Preview Stripe key disclosure:</span>{" "}
              {rec?.preview_stripe_key_disclosure}
            </p>
          </div>
          <p className="mt-2 text-[11px] text-gray-500"
             data-testid="cr-reserve-inclusion-rule">
            {rec?.reserve_inclusion_rule}
          </p>
          <p className="mt-1 text-[11px] text-gray-500">
            Authoritative Stripe-verified pass:{" "}
            <code className="text-purple-300">{rec?.authoritative_endpoint}</code>
          </p>
        </Section>

        {/* 5. Ledger Audit */}
        <Section id="ledger-audit" title="5. Ledger Audit"
                 subtitle="Safe identifiers only — no secrets, no PII.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Tile label="Event Count"
                  value={fmtNum(la?.event_count)}
                  testId="cr-tile-ledger-event-count" />
            <Tile label="Snapshot"
                  value={la?.snapshot?.present ? "Present" : "Absent"}
                  sub={la?.snapshot?.as_of || "—"}
                  tone={la?.snapshot?.present ? "default" : "warn"}
                  testId="cr-tile-snapshot" />
            <Tile label="Replay Hash Match"
                  value={la?.snapshot?.matches_current_replay === null
                          ? "n/a"
                          : la?.snapshot?.matches_current_replay ? "OK" : "MISMATCH"}
                  tone={la?.snapshot?.matches_current_replay === false ? "danger" : "default"}
                  testId="cr-tile-hash-match" />
          </div>
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <table className="w-full text-xs" data-testid="cr-ledger-tail">
              <thead className="bg-white/5 text-gray-300">
                <tr>
                  <th className="p-2 text-left">Timestamp</th>
                  <th className="p-2 text-left">Event Type</th>
                  <th className="p-2 text-left">Source</th>
                  <th className="p-2 text-right">USD Δ</th>
                  <th className="p-2 text-right">Paid Δ</th>
                  <th className="p-2 text-right">Liab Pend Δ</th>
                  <th className="p-2 text-right">Liab Avail Δ</th>
                </tr>
              </thead>
              <tbody>
                {(la?.recent_events || []).map((e) => (
                  <tr key={e.event_id}
                      className="border-t border-white/5"
                      data-testid={`cr-ledger-row-${e.event_id}`}>
                    <td className="p-2 text-gray-400">{e.occurred_at?.substring(0, 19).replace("T", " ")}</td>
                    <td className="p-2 text-white font-mono">{e.event_type}</td>
                    <td className="p-2 text-gray-300 font-mono text-[10px]">
                      {e.source?.kind || "—"}
                      {e.source?.payout_request_id ? ` · ${e.source.payout_request_id}` : ""}
                      {e.source?.gift_transaction_id ? ` · ${e.source.gift_transaction_id}` : ""}
                      {e.source?.coin_purchase_id ? ` · ${e.source.coin_purchase_id}` : ""}
                    </td>
                    <td className="p-2 text-right text-gray-200">{fmtUsdCents(e.usd_delta_cents)}</td>
                    <td className="p-2 text-right text-gray-200">{fmtNum(e.paid_coins_delta)}</td>
                    <td className="p-2 text-right text-gray-200">{fmtNum(e.liability_pending_delta)}</td>
                    <td className="p-2 text-right text-gray-200">{fmtNum(e.liability_available_delta)}</td>
                  </tr>
                ))}
                {(!la?.recent_events || la.recent_events.length === 0) && (
                  <tr><td colSpan={7} className="p-4 text-center text-gray-500"
                         data-testid="cr-ledger-empty">No ledger events yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Safety notes footer */}
        {data?.safety_notes && (
          <div className="mt-6 p-4 rounded-lg border border-white/10 bg-white/[0.02]"
               data-testid="cr-safety-notes">
            <p className="text-[11px] uppercase tracking-widest text-gray-400 mb-2">
              Safety contract
            </p>
            <ul className="text-[11px] text-gray-400 list-disc pl-5 space-y-1">
              {data.safety_notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
